/**
 * Module Editor - LMS
 * Edit individual course module
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

function ModuleEditor() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [courseTitle, setCourseTitle] = useState('');

  useEffect(() => {
    fetchModule();
  }, [courseId, moduleId]);

  const fetchModule = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Fetch course to get module details
      const response = await fetch(`/courses/${courseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch course');
      const courseData = await response.json();

      setCourseTitle(courseData.title || '');

      // Find the specific module
      const module = courseData.modules?.find(m => m.id === parseInt(moduleId));

      if (!module) {
        throw new Error('Module not found');
      }

      setTitle(module.title || '');
      setContent(module.content || '');
      setError(null);
    } catch (err) {
      console.error('Error fetching module:', err);
      setError('Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Module title is required');
      return;
    }

    if (!content.trim()) {
      alert('Module content is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const moduleData = {
        title,
        content
      };

      const response = await fetch(`/courses/modules/${moduleId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(moduleData)
      });

      if (!response.ok) throw new Error('Failed to save module');

      alert('Module saved successfully');
      navigate(`/courses/${courseId}`);
    } catch (err) {
      console.error('Error saving module:', err);
      alert('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginLeft: '8px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: '#fff',
    border: '2px solid #d4af37'
  };

  const sectionStyle = {
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px'
  };

  const labelStyle = {
    display: 'block',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '8px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '1rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '400px',
    fontFamily: 'monospace',
    fontSize: '0.95rem',
    lineHeight: '1.6'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={sectionStyle}>Loading module...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Edit Module</h1>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            Course: {courseTitle}
          </p>
        </div>
        <div>
          <button onClick={() => navigate(`/courses/${courseId}`)} style={secondaryButtonStyle}>
            Back to Course
          </button>
          <button onClick={handleSave} disabled={saving} style={buttonStyle}>
            {saving ? 'Saving...' : 'Save Module'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      <div style={sectionStyle}>
        <label style={labelStyle}>
          Module Title *
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            placeholder="Enter module title"
          />
        </label>

        <label style={labelStyle}>
          Module Content *
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
            Enter module content. You can use plain text or basic Markdown formatting.
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={textareaStyle}
            placeholder="Enter module content here..."
          />
        </label>

        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
          Character count: {content.length}
        </div>
      </div>

      <div style={sectionStyle}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a' }}>
          Preview
        </h3>
        <div style={{
          padding: '16px',
          background: '#f9f9f9',
          border: '1px solid #ddd',
          borderRadius: '4px',
          whiteSpace: 'pre-wrap',
          fontSize: '0.95rem',
          lineHeight: '1.6'
        }}>
          {content || <span style={{ color: '#999' }}>No content to preview</span>}
        </div>
      </div>
    </div>
  );
}

export default ModuleEditor;
